// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  extentFromValues,
} from "../core/math.js";
import {
  formatElapsedTime,
} from "../core/format.js";
import {
  scoreClass,
} from "../core/score.js";

function titleCase(value) {
  return String(value ?? "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function pdfSafeText(value) {
  return String(value ?? "")
    .replace(/[·•]/g, "-")
    .replace(/[–—]/g, "-")
    .replace(/→/g, ">")
    .replace(/[°]/g, " deg")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, "");
}

export function escapePdfString(value) {
  return pdfSafeText(value).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}

export const pdfPalette = {
  ink: [0.07, 0.10, 0.16],
  muted: [0.35, 0.43, 0.52],
  quiet: [0.55, 0.62, 0.70],
  rule: [0.82, 0.87, 0.92],
  panel: [0.97, 0.98, 1.00],
  panelAlt: [0.95, 0.97, 0.99],
  navy: [0.04, 0.09, 0.16],
  blue: [0.18, 0.53, 1.00],
  green: [0.17, 0.58, 0.34],
  amber: [0.92, 0.56, 0.08],
  red: [0.78, 0.20, 0.18],
  purple: [0.48, 0.35, 0.95],
  teal: [0.10, 0.57, 0.54],
  white: [1, 1, 1],
};

export function pdfColorCommand(color, operator) {
  return `${color.map((value) => Number(value).toFixed(3)).join(" ")} ${operator}`;
}

export function pdfColorForScore(score) {
  const className = scoreClass(score);
  if (className === "red") return pdfPalette.red;
  if (className === "amber") return pdfPalette.amber;
  if (className === "green") return pdfPalette.green;
  return pdfPalette.quiet;
}

export function pdfSoftColorForScore(score) {
  const className = scoreClass(score);
  if (className === "red") return [1.00, 0.94, 0.93];
  if (className === "amber") return [1.00, 0.97, 0.89];
  if (className === "green") return [0.93, 0.98, 0.95];
  return pdfPalette.panel;
}

export function pdfTruncateText(text, size, width) {
  const safe = pdfSafeText(text);
  const maxChars = Math.max(4, Math.floor(width / (size * 0.52)));
  if (safe.length <= maxChars) return safe;
  return `${safe.slice(0, Math.max(0, maxChars - 3)).trim()}...`;
}

export function binaryStringFromDataUrl(dataUrl) {
  const [, meta = "", payload = ""] = String(dataUrl ?? "").match(/^data:([^,]*),(.*)$/) ?? [];
  if (!payload) return "";
  return /;base64/i.test(meta) ? atob(payload) : decodeURIComponent(payload);
}

export function asciiHexFromDataUrl(dataUrl) {
  const binary = binaryStringFromDataUrl(dataUrl);
  if (!binary) return "";
  let hex = "";
  for (let index = 0; index < binary.length; index += 1) {
    hex += binary.charCodeAt(index).toString(16).padStart(2, "0");
  }
  return `${hex}>`;
}

export function buildPdfBlob(pages) {
  const objects = [];
  const addObject = (body) => {
    objects.push(body);
    return objects.length;
  };
  const catalogId = addObject("<< /Type /Catalog /Pages 2 0 R >>");
  const pagesId = addObject("PAGES_PLACEHOLDER");
  const fontId = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>");
  const boldFontId = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>");
  const pageIds = [];
  const imageResources = new Map();
  pages.forEach((page) => {
    (page._images ?? []).forEach((image) => {
      if (imageResources.has(image.name)) return;
      const asciiHex = asciiHexFromDataUrl(image.dataUrl);
      if (!asciiHex) return;
      const imageId = addObject(`<< /Type /XObject /Subtype /Image /Width ${Math.round(image.pixelWidth)} /Height ${Math.round(image.pixelHeight)} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter [/ASCIIHexDecode /DCTDecode] /Length ${asciiHex.length} >>\nstream\n${asciiHex}\nendstream`);
      imageResources.set(image.name, imageId);
    });
  });
  pages.forEach((page, index) => {
    const pageNumber = index + 1;
    const footer = [
      "0.55 0.62 0.70 rg",
      `BT /F1 8 Tf 1 0 0 1 54 30 Tm (${escapePdfString(`Alary IQ advisory report - page ${pageNumber} of ${pages.length}`)}) Tj ET`,
    ];
    const stream = [...page, ...footer].join("\n");
    const contentId = addObject(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    const xObjects = (page._images ?? [])
      .map((image) => imageResources.has(image.name) ? `/${image.name} ${imageResources.get(image.name)} 0 R` : "")
      .filter(Boolean)
      .join(" ");
    const xObjectResources = xObjects ? ` /XObject << ${xObjects} >>` : "";
    const pageId = addObject(`<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 ${fontId} 0 R /F2 ${boldFontId} 0 R >>${xObjectResources} >> /Contents ${contentId} 0 R >>`);
    pageIds.push(pageId);
  });
  objects[pagesId - 1] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`;
  objects[catalogId - 1] = `<< /Type /Catalog /Pages ${pagesId} 0 R >>`;

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((body, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return new Blob([pdf], { type: "application/pdf" });
}

export function makePdfWriter({ profileDurationBuckets = () => [] } = {}) {
  const width = 612;
  const height = 792;
  const margin = 54;
  const contentWidth = width - margin * 2;
  const pages = [];
  let imageCounter = 0;
  let page = [];
  let y = height - margin;
  const newPage = () => {
    page = [];
    page._images = [];
    pages.push(page);
    y = height - margin;
  };
  const ensureSpace = (space) => {
    if (y - space < margin) newPage();
  };
  const reserve = (space) => {
    ensureSpace(space);
    const top = y;
    y -= space;
    return top;
  };
  const setFill = (color) => {
    page.push(pdfColorCommand(color, "rg"));
  };
  const setStroke = (color) => {
    page.push(pdfColorCommand(color, "RG"));
  };
  const drawRect = (x, bottom, rectWidth, rectHeight, options = {}) => {
    if (options.lineWidth) page.push(`${options.lineWidth.toFixed(2)} w`);
    if (options.fill) setFill(options.fill);
    if (options.stroke) setStroke(options.stroke);
    const operator = options.fill && options.stroke ? "B" : options.fill ? "f" : "S";
    page.push(`${x.toFixed(1)} ${bottom.toFixed(1)} ${rectWidth.toFixed(1)} ${rectHeight.toFixed(1)} re ${operator}`);
  };
  const drawLine = (x1, y1, x2, y2, options = {}) => {
    setStroke(options.color ?? pdfPalette.rule);
    page.push(`${(options.width ?? 0.8).toFixed(2)} w`);
    page.push(`${x1.toFixed(1)} ${y1.toFixed(1)} m ${x2.toFixed(1)} ${y2.toFixed(1)} l S`);
  };
  const drawDot = (cx, cy, r, color) => {
    setFill(color);
    const k = 0.5522847498 * r;
    page.push(`${(cx + r).toFixed(2)} ${cy.toFixed(2)} m`);
    page.push(`${(cx + r).toFixed(2)} ${(cy + k).toFixed(2)} ${(cx + k).toFixed(2)} ${(cy + r).toFixed(2)} ${cx.toFixed(2)} ${(cy + r).toFixed(2)} c`);
    page.push(`${(cx - k).toFixed(2)} ${(cy + r).toFixed(2)} ${(cx - r).toFixed(2)} ${(cy + k).toFixed(2)} ${(cx - r).toFixed(2)} ${cy.toFixed(2)} c`);
    page.push(`${(cx - r).toFixed(2)} ${(cy - k).toFixed(2)} ${(cx - k).toFixed(2)} ${(cy - r).toFixed(2)} ${cx.toFixed(2)} ${(cy - r).toFixed(2)} c`);
    page.push(`${(cx + k).toFixed(2)} ${(cy - r).toFixed(2)} ${(cx + r).toFixed(2)} ${(cy - k).toFixed(2)} ${(cx + r).toFixed(2)} ${cy.toFixed(2)} c`);
    page.push("f");
  };
  const helveticaWidths = {
    " ": 278,
    "!": 278,
    "\"": 355,
    "#": 556,
    "$": 556,
    "%": 889,
    "&": 667,
    "'": 222,
    "(": 333,
    ")": 333,
    "*": 389,
    "+": 584,
    ",": 278,
    "-": 333,
    ".": 278,
    "/": 278,
    "0": 556,
    "1": 556,
    "2": 556,
    "3": 556,
    "4": 556,
    "5": 556,
    "6": 556,
    "7": 556,
    "8": 556,
    "9": 556,
    ":": 278,
    ";": 278,
    "<": 584,
    "=": 584,
    ">": 584,
    "?": 556,
    "@": 1015,
    A: 667,
    B: 667,
    C: 722,
    D: 722,
    E: 667,
    F: 611,
    G: 778,
    H: 722,
    I: 278,
    J: 500,
    K: 667,
    L: 556,
    M: 833,
    N: 722,
    O: 778,
    P: 667,
    Q: 778,
    R: 722,
    S: 667,
    T: 611,
    U: 722,
    V: 667,
    W: 944,
    X: 667,
    Y: 667,
    Z: 611,
    "[": 278,
    "\\": 278,
    "]": 278,
    "^": 469,
    _: 556,
    "`": 222,
    a: 556,
    b: 556,
    c: 500,
    d: 556,
    e: 556,
    f: 278,
    g: 556,
    h: 556,
    i: 222,
    j: 222,
    k: 500,
    l: 222,
    m: 833,
    n: 556,
    o: 556,
    p: 556,
    q: 556,
    r: 333,
    s: 500,
    t: 278,
    u: 556,
    v: 500,
    w: 722,
    x: 500,
    y: 500,
    z: 500,
    "{": 334,
    "|": 260,
    "}": 334,
    "~": 584,
  };
  const estimatedTextWidth = (text, size) => [...pdfSafeText(text)]
    .reduce((sum, char) => sum + (helveticaWidths[char] ?? 556) * size / 1000, 0);
  const justifiedWordSpacing = (line, size, usableWidth) => {
    const safe = pdfSafeText(line).trim();
    const gapCount = (safe.match(/ /g) ?? []).length;
    if (gapCount < 1) return 0;
    const extraWidth = usableWidth - estimatedTextWidth(safe, size);
    return extraWidth > 0 ? extraWidth / gapCount : 0;
  };
  const textCommand = (text, x, baseline, size, bold, color, options = {}) => {
    const [r, g, b] = color;
    const wordSpacing = Number.isFinite(options.wordSpacing) ? Math.max(0, options.wordSpacing) : 0;
    page.push(`${r} ${g} ${b} rg`);
    page.push(`BT /${bold ? "F2" : "F1"} ${size} Tf ${wordSpacing.toFixed(3)} Tw 1 0 0 1 ${x.toFixed(1)} ${baseline.toFixed(1)} Tm (${escapePdfString(text)}) Tj ET`);
  };
  const wrap = (text, size, usableWidth) => {
    const words = pdfSafeText(text).split(/\s+/).filter(Boolean);
    const lines = [];
    let current = "";
    words.forEach((word) => {
      const next = current ? `${current} ${word}` : word;
      if (estimatedTextWidth(next, size) > usableWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = next;
      }
    });
    if (current) lines.push(current);
    return lines.length ? lines : [""];
  };
  const drawWrapped = (text, x, top, size, usableWidth, options = {}) => {
    const leading = options.leading ?? size + 3;
    const lines = wrap(text, size, usableWidth);
    const maxLines = options.maxLines ?? lines.length;
    let baseline = top;
    const visibleLines = lines.slice(0, maxLines);
    visibleLines.forEach((line, index) => {
      const isLastVisible = index === maxLines - 1 && lines.length > maxLines;
      const renderedLine = isLastVisible ? pdfTruncateText(line, size, usableWidth) : line;
      const shouldJustify = options.justify && !options.bold && !isLastVisible && index < visibleLines.length - 1;
      textCommand(
        renderedLine,
        x,
        baseline,
        size,
        Boolean(options.bold),
        options.color ?? pdfPalette.ink,
        { wordSpacing: shouldJustify ? justifiedWordSpacing(renderedLine, size, usableWidth) : 0 },
      );
      baseline -= leading;
    });
    return baseline;
  };
  const wrappedHeight = (text, size, usableWidth, options = {}) => {
    const leading = options.leading ?? size + 3;
    const maxLines = options.maxLines ?? Infinity;
    return Math.min(wrap(text, size, usableWidth).length, maxLines) * leading;
  };
  const scoreBar = (x, baseline, barWidth, score, options = {}) => {
    const barHeight = options.height ?? 5;
    drawRect(x, baseline, barWidth, barHeight, { fill: [0.88, 0.91, 0.94] });
    if (Number.isFinite(score)) {
      drawRect(x, baseline, barWidth * Math.max(0, Math.min(100, score)) / 100, barHeight, { fill: pdfColorForScore(score) });
    }
  };
  const bandColor = (band) => {
    if (band === "review" || band === "425+") return pdfPalette.red;
    if (band === "warm" || band === "400-424") return pdfPalette.amber;
    if (band === "elevated" || band === "375-399" || band === "350-374") return pdfPalette.teal;
    if (band === "325-349") return pdfPalette.blue;
    if (band === "fit") return pdfPalette.amber;
    return [0.58, 0.66, 0.76];
  };
  const formatNumber = (value, digits = 0, suffix = "") => (
    Number.isFinite(value) ? `${Number(value).toFixed(digits)}${suffix}` : "--"
  );
  const sparklinePath = (values, x, bottom, sparkWidth, sparkHeight, color) => {
    const finite = values.filter(Number.isFinite);
    if (finite.length < 2) return false;
    const extent = extentFromValues(finite);
    const span = Math.max(1, (extent?.max ?? 1) - (extent?.min ?? 0));
    const maxPoints = 95;
    const step = Math.max(1, Math.ceil(values.length / maxPoints));
    const sampled = values
      .map((value, index) => ({ value, index }))
      .filter((sample) => Number.isFinite(sample.value) && sample.index % step === 0);
    const lastFiniteIndex = values.map((value, index) => Number.isFinite(value) ? index : null).filter(Number.isFinite).at(-1);
    if (Number.isFinite(lastFiniteIndex) && sampled.at(-1)?.index !== lastFiniteIndex) {
      sampled.push({ value: values[lastFiniteIndex], index: lastFiniteIndex });
    }
    if (sampled.length < 2) return false;
    setStroke(color);
    page.push("1.05 w");
    sampled.forEach((sample, index) => {
      const ratio = values.length <= 1 ? 0 : sample.index / (values.length - 1);
      const px = x + ratio * sparkWidth;
      const py = bottom + ((sample.value - extent.min) / span) * sparkHeight;
      page.push(`${px.toFixed(1)} ${py.toFixed(1)} ${index ? "l" : "m"}`);
    });
    page.push("S");
    return true;
  };
  const drawSeriesPath = (series, xFor, yFor, options = {}) => {
    const points = (Array.isArray(series) ? series : [])
      .filter((point) => Number.isFinite(point?.t) && Number.isFinite(point?.value));
    if (points.length < 2) return false;
    setStroke(options.color ?? pdfPalette.blue);
    page.push(`${(options.width ?? 1.1).toFixed(2)} w`);
    if (options.dash) page.push(`[${options.dash.join(" ")}] 0 d`);
    points.forEach((point, index) => {
      page.push(`${xFor(point.t).toFixed(1)} ${yFor(point.value).toFixed(1)} ${index ? "l" : "m"}`);
    });
    page.push("S");
    if (options.dash) page.push("[] 0 d");
    return true;
  };
  const valuesExtent = (values, fallback = { min: 0, max: 1 }) => {
    const finiteValues = (values ?? []).filter(Number.isFinite);
    if (!finiteValues.length) return fallback;
    const min = Math.min(...finiteValues);
    const max = Math.max(...finiteValues);
    return min === max ? { min: min - 1, max: max + 1 } : { min, max };
  };
  // Everything drawn inside `draw` is clipped to the plot rectangle, so a
  // series or reference line that lands outside the computed axis range can
  // never stroke across neighbouring tiles, statistics, or the next panel.
  const withPlotClip = (plotLeft, plotBottom, plotWidth, plotHeight, draw) => {
    page.push("q");
    page.push(`${plotLeft.toFixed(1)} ${plotBottom.toFixed(1)} ${plotWidth.toFixed(1)} ${plotHeight.toFixed(1)} re W n`);
    draw();
    page.push("Q");
  };
  const drawImage = (image, x, bottom, imageWidth, imageHeight) => {
    if (!image?.dataUrl || !Number.isFinite(image.pixelWidth) || !Number.isFinite(image.pixelHeight)) return;
    const name = `Im${++imageCounter}`;
    page._images.push({
      name,
      dataUrl: image.dataUrl,
      mimeType: image.mimeType ?? "image/jpeg",
      pixelWidth: image.pixelWidth,
      pixelHeight: image.pixelHeight,
    });
    page.push("q");
    page.push(`${imageWidth.toFixed(1)} 0 0 ${imageHeight.toFixed(1)} ${x.toFixed(1)} ${bottom.toFixed(1)} cm`);
    page.push(`/${name} Do`);
    page.push("Q");
  };
  const drawVisualStats = (stats, x, top, availableWidth) => {
    const visible = (stats ?? []).filter(Boolean).slice(0, 4);
    if (!visible.length) return 0;
    const gap = 7;
    const tileWidth = (availableWidth - gap * (visible.length - 1)) / visible.length;
    const tileHeight = 37;
    visible.forEach((stat, index) => {
      const tileX = x + index * (tileWidth + gap);
      const bottom = top - tileHeight;
      drawRect(tileX, bottom, tileWidth, tileHeight, { fill: pdfPalette.panel, stroke: pdfPalette.rule, lineWidth: 0.3 });
      textCommand(pdfTruncateText(stat.label ?? "", 6.6, tileWidth - 12).toUpperCase(), tileX + 6, top - 11, 6.6, true, pdfPalette.muted);
      textCommand(pdfTruncateText(stat.value ?? "--", 10.4, tileWidth - 12), tileX + 6, top - 26, 10.4, true, stat.color ?? pdfPalette.ink);
      if (stat.detail) textCommand(pdfTruncateText(stat.detail, 6.5, tileWidth - 12), tileX + 6, bottom + 5, 6.5, false, pdfPalette.quiet);
    });
    return tileHeight + 9;
  };
  // Tile grids render the full roster they are given (a twelve-cylinder
  // engine gets three rows of four); callers that intentionally show a subset
  // trim the model themselves and disclose the count in a stat tile.
  const drawVisualTileGrid = (tiles, x, top, availableWidth, options = {}) => {
    const visible = (tiles ?? []).filter(Boolean).slice(0, options.limit ?? Infinity);
    if (!visible.length) return 0;
    const columns = Math.min(options.columns ?? 4, Math.max(1, visible.length));
    const gap = 7;
    const tileWidth = (availableWidth - gap * (columns - 1)) / columns;
    const tileHeight = options.tileHeight ?? 42;
    visible.forEach((tile, index) => {
      const col = index % columns;
      const row = Math.floor(index / columns);
      const tileX = x + col * (tileWidth + gap);
      const tileTop = top - row * (tileHeight + gap);
      const bottom = tileTop - tileHeight;
      const accent = bandColor(tile.band);
      drawRect(tileX, bottom, tileWidth, tileHeight, { fill: pdfPalette.panel, stroke: pdfPalette.rule, lineWidth: 0.3 });
      drawRect(tileX, bottom, 3, tileHeight, { fill: accent });
      textCommand(pdfTruncateText(tile.label ?? "", 6.7, tileWidth - 12).toUpperCase(), tileX + 8, tileTop - 11, 6.7, true, pdfPalette.muted);
      textCommand(pdfTruncateText(tile.value ?? "--", 10.2, tileWidth - 12), tileX + 8, tileTop - 26, 10.2, true, accent);
      if (tile.detail) textCommand(pdfTruncateText(tile.detail, 6.4, tileWidth - 12), tileX + 8, bottom + 5, 6.4, false, pdfPalette.quiet);
    });
    return Math.ceil(visible.length / columns) * tileHeight + Math.max(0, Math.ceil(visible.length / columns) - 1) * gap + 9;
  };
  const heatRowHeight = 13;
  const heatLegendHeight = 16;
  // Draws the given heat rows (all of them; pagination is the panel's job) and
  // the band legend beneath them. Returns nothing; the panel derives its height
  // from the same constants before reserving space.
  const drawHeatVisual = (visual, x, top, availableWidth, options = {}) => {
    let cursor = top;
    if (options.stats !== false) cursor -= drawVisualStats(visual.model?.stats, x, cursor, availableWidth);
    const rows = options.rows ?? (visual.model?.rows ?? []);
    if (!rows.length) return;
    const labelWidth = 56;
    const barX = x + labelWidth;
    const barWidth = availableWidth - labelWidth - 72;
    const rowHeight = heatRowHeight;
    const maxMinutes = Math.max(1, ...(options.scaleRows ?? rows).map((row) => row.operatingMinutes ?? 0));
    rows.forEach((row, index) => {
      const rowY = cursor - index * rowHeight;
      textCommand(pdfTruncateText(row.label ?? "", 7.4, labelWidth - 6), x, rowY - 8, 7.4, true, pdfPalette.ink);
      drawRect(barX, rowY - 11, barWidth, 7, { fill: [0.90, 0.93, 0.96] });
      let offset = 0;
      (row.bands ?? []).forEach((band) => {
        const minutes = Number(band.minutes) || 0;
        if (minutes <= 0) return;
        const segmentWidth = Math.max(0.6, barWidth * minutes / maxMinutes);
        drawRect(barX + offset, rowY - 11, segmentWidth, 7, { fill: bandColor(band.key) });
        offset += segmentWidth;
      });
      textCommand(`${Math.round(row.operatingMinutes ?? 0)} min`, barX + barWidth + 8, rowY - 8, 7.1, false, pdfPalette.muted);
    });
    const legendY = cursor - rows.length * rowHeight - 4;
    const legend = [["<325", "below-325"], ["325+", "325-349"], ["375+", "375-399"], ["400+", "400-424"], ["425+", "425+"]];
    let legendX = barX;
    legend.forEach(([label, key]) => {
      drawRect(legendX, legendY - 5, 12, 5, { fill: bandColor(key) });
      textCommand(label, legendX + 16, legendY - 4, 6.6, false, pdfPalette.quiet);
      legendX += 50;
    });
  };
  const drawOilVisual = (visual, x, top, availableWidth) => {
    const model = visual.model ?? {};
    const stats = [
      { label: "Operating pressure", value: formatNumber(model.pressureMedian, 0, " psi"), detail: `${formatNumber(model.pressureP10, 0)}-${formatNumber(model.pressureP90, 0)} psi warm range` },
      { label: "Oil temp", value: formatNumber(model.temperatureMedian, 0, " F"), detail: `${formatNumber(model.temperatureP10, 0)}-${formatNumber(model.temperatureP90, 0)} F` },
      { label: "Status", value: Number.isFinite(model.score) ? `${Math.round(model.score)}/100` : "Logged", detail: model.note ?? `${model.sampleCount ?? 0} samples` },
    ];
    let cursor = top - drawVisualStats(stats, x, top, availableWidth);
    const plotTop = cursor - 2;
    const plotHeight = 94;
    const plotBottom = plotTop - plotHeight;
    const plotLeft = x + 46;
    const plotWidth = availableWidth - 94;
    const samples = (model.samples ?? []).filter((sample) => Number.isFinite(sample.t));
    const pressureSeries = samples
      .filter((sample) => Number.isFinite(sample.pressure))
      .map((sample) => ({ t: sample.t, value: sample.pressure }));
    const tempSeries = samples
      .filter((sample) => Number.isFinite(sample.temperature))
      .map((sample) => ({ t: sample.t, value: sample.temperature }));
    const tExtent = valuesExtent(samples.map((sample) => sample.t), { min: 0, max: 1 });
    const pressureExtent = valuesExtent([
      ...pressureSeries.map((sample) => sample.value),
      model.pressureP10,
      model.pressureP90,
      model.pressureMedian,
    ], { min: 20, max: 110 });
    const tempExtent = valuesExtent(tempSeries.map((sample) => sample.value), { min: 120, max: 220 });
    const pressureMin = Math.max(0, Math.floor((pressureExtent.min - 5) / 5) * 5);
    const pressureMax = Math.ceil((pressureExtent.max + 5) / 5) * 5;
    const tempMin = Math.floor((tempExtent.min - 5) / 5) * 5;
    const tempMax = Math.ceil((tempExtent.max + 5) / 5) * 5;
    const xFor = (value) => plotLeft + (value - tExtent.min) / Math.max(1, tExtent.max - tExtent.min) * plotWidth;
    const pressureYFor = (value) => plotBottom + (value - pressureMin) / Math.max(1, pressureMax - pressureMin) * plotHeight;
    const tempYFor = (value) => plotBottom + (value - tempMin) / Math.max(1, tempMax - tempMin) * plotHeight;
    drawRect(plotLeft, plotBottom, plotWidth, plotHeight, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.35 });
    withPlotClip(plotLeft, plotBottom, plotWidth, plotHeight, () => {
      if (Number.isFinite(model.pressureP10) && Number.isFinite(model.pressureP90)) {
        const y1 = pressureYFor(model.pressureP10);
        const y2 = pressureYFor(model.pressureP90);
        drawRect(plotLeft, Math.min(y1, y2), plotWidth, Math.max(2, Math.abs(y2 - y1)), { fill: [0.90, 0.98, 0.95] });
        textCommand("warm pressure range", plotLeft + 6, Math.max(y1, y2) - 8, 6.2, true, pdfPalette.green);
      }
      if (Number.isFinite(model.pressureMedian)) {
        drawLine(plotLeft, pressureYFor(model.pressureMedian), plotLeft + plotWidth, pressureYFor(model.pressureMedian), { color: pdfPalette.green, width: 0.5 });
      }
      drawSeriesPath(tempSeries, xFor, tempYFor, { color: pdfPalette.amber, width: 1.0 });
      drawSeriesPath(pressureSeries, xFor, pressureYFor, { color: pdfPalette.blue, width: 1.25 });
    });
    const legendY = plotTop - 8;
    drawLine(plotLeft + plotWidth - 116, legendY, plotLeft + plotWidth - 96, legendY, { color: pdfPalette.blue, width: 1.2 });
    textCommand("Oil pressure", plotLeft + plotWidth - 90, legendY - 2, 6.5, true, pdfPalette.blue);
    drawLine(plotLeft + plotWidth - 42, legendY, plotLeft + plotWidth - 22, legendY, { color: pdfPalette.amber, width: 1.0 });
    textCommand("Temp", plotLeft + plotWidth - 16, legendY - 2, 6.5, true, pdfPalette.amber);
    textCommand(`${Math.round(pressureMax)} psi`, x, plotBottom + plotHeight - 4, 6.6, false, pdfPalette.quiet);
    textCommand(`${Math.round(pressureMin)} psi`, x, plotBottom, 6.6, false, pdfPalette.quiet);
    textCommand(`${Math.round(tempMax)} F`, plotLeft + plotWidth + 8, plotBottom + plotHeight - 4, 6.6, false, pdfPalette.quiet);
    textCommand(`${Math.round(tempMin)} F`, plotLeft + plotWidth + 8, plotBottom, 6.6, false, pdfPalette.quiet);
    textCommand(formatElapsedTime(tExtent.min), plotLeft, plotBottom - 12, 6.6, false, pdfPalette.quiet);
    textCommand(formatElapsedTime(tExtent.max), plotLeft + plotWidth - 26, plotBottom - 12, 6.6, false, pdfPalette.quiet);
    textCommand("elapsed time", plotLeft + plotWidth / 2 - 28, plotBottom - 12, 6.8, true, pdfPalette.muted);
  };
  const drawTraceVisual = (visual, x, top, availableWidth, options = {}) => {
    const model = visual.model ?? {};
    let cursor = top;
    if (options.stats !== false) cursor -= drawVisualStats(model.stats, x, cursor, availableWidth);
    const trace = options.trace ?? model.trace ?? model;
    const samples = trace.samples ?? trace.residualSeries ?? [];
    if (!samples.length) return;
    const plotTop = cursor - 2;
    const plotHeight = options.height ?? 84;
    const plotBottom = plotTop - plotHeight;
    const plotLeft = x + 42;
    const plotWidth = availableWidth - 58;
    const finiteValuesOf = (series) => (Array.isArray(series) ? series : [])
      .map((point) => point?.value)
      .filter(Number.isFinite);
    const values = finiteValuesOf(samples);
    const thresholds = trace.thresholds ?? [];
    // Every series and reference drawn against the primary axis feeds its
    // range: the peer trace, the fitted series, the rhythm floor lines, and
    // the thresholds. A peer that runs entirely outside the selected trace's
    // range used to be plotted off the chart and over the tiles above it.
    const floorHalf = Number.isFinite(model.floorF) ? Math.abs(model.floorF) / 2 : null;
    const extent = valuesExtent([
      ...values,
      ...finiteValuesOf(trace.peerSamples),
      ...finiteValuesOf(model.fitSeries),
      ...thresholds.map((item) => item.value).filter(Number.isFinite),
      ...(floorHalf === null ? [] : [floorHalf, -floorHalf]),
    ], { min: -20, max: 20 });
    const padding = Math.max(3, (extent.max - extent.min) * 0.1);
    const yMin = extent.min - padding;
    const yMax = extent.max + padding;
    const tExtent = valuesExtent(samples.map((point) => point.t), { min: 0, max: 1 });
    const xFor = (value) => plotLeft + (value - tExtent.min) / Math.max(1, tExtent.max - tExtent.min) * plotWidth;
    const yFor = (value) => plotBottom + (value - yMin) / Math.max(1, yMax - yMin) * plotHeight;
    drawRect(plotLeft, plotBottom, plotWidth, plotHeight, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.35 });
    const secondaryValues = finiteValuesOf(trace.secondarySamples);
    const secondaryExtent = secondaryValues.length
      ? valuesExtent([...secondaryValues, ...finiteValuesOf(trace.secondaryPeerSamples)], { min: 0, max: 1 })
      : null;
    const secondaryPadding = secondaryExtent ? Math.max(3, (secondaryExtent.max - secondaryExtent.min) * 0.1) : 0;
    const secondaryMin = secondaryExtent ? secondaryExtent.min - secondaryPadding : 0;
    const secondaryMax = secondaryExtent ? secondaryExtent.max + secondaryPadding : 1;
    const secondaryFor = (value) => plotBottom + (value - secondaryMin) / Math.max(1, secondaryMax - secondaryMin) * plotHeight;
    withPlotClip(plotLeft, plotBottom, plotWidth, plotHeight, () => {
      thresholds.forEach((threshold) => {
        drawLine(plotLeft, yFor(threshold.value), plotLeft + plotWidth, yFor(threshold.value), { color: bandColor(threshold.band), width: 0.55 });
        textCommand(pdfTruncateText(threshold.label, 6.5, 80), plotLeft + plotWidth - 78, yFor(threshold.value) + 3, 6.5, true, bandColor(threshold.band));
      });
      if (trace.event && Number.isFinite(trace.event.startTime) && Number.isFinite(trace.event.endTime)) {
        const start = xFor(trace.event.startTime);
        const end = xFor(trace.event.endTime);
        drawRect(Math.max(plotLeft, start), plotBottom, Math.max(2, Math.min(plotLeft + plotWidth, end) - Math.max(plotLeft, start)), plotHeight, { fill: [0.94, 0.96, 1.00] });
      }
      if (floorHalf !== null) {
        drawLine(plotLeft, yFor(floorHalf), plotLeft + plotWidth, yFor(floorHalf), { color: pdfPalette.amber, width: 0.5 });
        drawLine(plotLeft, yFor(-floorHalf), plotLeft + plotWidth, yFor(-floorHalf), { color: pdfPalette.amber, width: 0.5 });
      }
      if (trace.peerSamples?.length) {
        drawSeriesPath(trace.peerSamples, xFor, yFor, { color: [0.72, 0.78, 0.86], width: 0.75, dash: [3, 2] });
      }
      drawSeriesPath(samples, xFor, yFor, { color: options.color ?? pdfPalette.blue, width: 1.15 });
      if (secondaryExtent) {
        if (trace.secondaryPeerSamples?.length) {
          drawSeriesPath(trace.secondaryPeerSamples, xFor, secondaryFor, { color: [0.82, 0.72, 0.65], width: 0.75, dash: [3, 2] });
        }
        drawSeriesPath(trace.secondarySamples, xFor, secondaryFor, { color: pdfPalette.amber, width: 1.05 });
      }
      if (model.fitSeries?.length) {
        drawSeriesPath(model.fitSeries, xFor, yFor, { color: pdfPalette.amber, width: 0.9, dash: [3, 2] });
      }
    });
    if (secondaryExtent) {
      textCommand(`${Math.round(secondaryMax)} ${trace.secondaryLabel ?? ""}`, plotLeft + plotWidth + 5, plotBottom + plotHeight - 4, 6.2, false, pdfPalette.quiet);
      textCommand(`${Math.round(secondaryMin)} ${trace.secondaryLabel ?? ""}`, plotLeft + plotWidth + 5, plotBottom, 6.2, false, pdfPalette.quiet);
    }
    textCommand(pdfTruncateText(trace.label ?? model.label ?? visual.title, 7.2, 120), plotLeft, plotTop - 10, 7.2, true, pdfPalette.ink);
    textCommand(`${Math.round(yMax)} ${trace.yLabel ?? "F"}`, x, plotBottom + plotHeight - 4, 6.5, false, pdfPalette.quiet);
    textCommand(`${Math.round(yMin)} ${trace.yLabel ?? "F"}`, x, plotBottom, 6.5, false, pdfPalette.quiet);
    textCommand("time", plotLeft + plotWidth - 16, plotBottom - 12, 6.6, false, pdfPalette.quiet);
  };
  // Panel chrome shared by every overview visual: reserves the height (moving
  // to a new page when needed), draws the card, title, and subtitle, and hands
  // back the content origin.
  const openVisualPanel = (visual, panelHeight, options = {}) => {
    const top = reserve(panelHeight + 10);
    const bottom = top - panelHeight;
    const innerX = margin + 14;
    const innerWidth = contentWidth - 28;
    drawRect(margin, bottom, contentWidth, panelHeight, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.5 });
    const title = `${visual.title ?? "Overview visual"}${options.titleSuffix ?? ""}`;
    textCommand(pdfTruncateText(title, 10.6, innerWidth), innerX, top - 17, 10.6, true, pdfPalette.ink);
    if (visual.subtitle) {
      textCommand(pdfTruncateText(visual.subtitle, 7.4, innerWidth), innerX, top - 31, 7.4, false, pdfPalette.muted);
    }
    return { top, bottom, innerX, innerWidth, contentTop: top - 44 };
  };
  // CHT heat exposure: the panel height follows the roster instead of a fixed
  // 154pt, and a roster too tall for one page continues in a second panel so
  // every cylinder's bar is drawn (a twelve-cylinder engine used to lose rows
  // nine to twelve while the summary still named cylinder 12 as hottest).
  const drawHeatPanels = (visual) => {
    const allRows = visual.model?.rows ?? [];
    const statsHeight = (visual.model?.stats ?? []).length ? 46 : 0;
    const fixedHeight = 44 + heatLegendHeight + 14;
    const maxPanelHeight = height - margin * 2 - 10;
    const rowsPerPanel = Math.max(1, Math.floor((maxPanelHeight - fixedHeight - statsHeight) / heatRowHeight));
    const chunks = [];
    for (let index = 0; index < allRows.length; index += rowsPerPanel) chunks.push(allRows.slice(index, index + rowsPerPanel));
    if (!chunks.length) chunks.push([]);
    chunks.forEach((rows, chunkIndex) => {
      const showStats = chunkIndex === 0;
      const panelHeight = fixedHeight + (showStats ? statsHeight : 0) + rows.length * heatRowHeight;
      const { innerX, innerWidth, contentTop } = openVisualPanel(visual, panelHeight, {
        titleSuffix: chunkIndex ? ` (continued ${chunkIndex + 1} of ${chunks.length})` : "",
      });
      drawHeatVisual(visual, innerX, contentTop, innerWidth, { rows, stats: showStats, scaleRows: allRows });
      y -= 1;
    });
  };
  const drawOverviewVisualPanel = (visual) => {
    if (visual.type === "heat-bars") {
      drawHeatPanels(visual);
      return;
    }
    const hasTrace = Boolean(visual.model?.trace || visual.model?.residualSeries?.length);
    const tileCount = (visual.model?.tiles ?? []).filter(Boolean).length;
    const tileRowsFor = (limit = Infinity) => Math.ceil(Math.min(tileCount, limit) / 4);
    const tileHeightFor = (limit = Infinity) => {
      const rows = tileRowsFor(limit);
      return rows ? rows * 42 + Math.max(0, rows - 1) * 7 + 9 : 0;
    };
    const statsHeight = (visual.model?.stats ?? []).length ? 46 : 0;
    const traceHeight = (heightValue) => heightValue + 16;
    const panelHeight = visual.type === "electrical" ? 44 + statsHeight + tileHeightFor(4) + traceHeight(50) + (visual.model?.startTrace ? traceHeight(48) + 10 : 0) + 14
      : visual.type === "oil-envelope" || visual.type === "oil-trend" ? 210
        : visual.type === "sensor-trust" ? 44 + statsHeight + tileHeightFor() + 14
        : visual.type === "cooling-margin" || visual.type === "cooling-rate" || visual.type === "egt-balance" || visual.type === "pairing"
          ? 44 + statsHeight + tileHeightFor() + (hasTrace ? traceHeight(68) : 0) + 14
          : hasTrace ? 194 : 116;
    const { innerX, innerWidth, contentTop } = openVisualPanel(visual, panelHeight);
    if (visual.type === "oil-envelope" || visual.type === "oil-trend") drawOilVisual(visual, innerX, contentTop, innerWidth);
    else if (visual.type === "waveform") drawTraceVisual(visual, innerX, contentTop, innerWidth, { color: pdfPalette.amber, height: 82 });
    else if (visual.type === "cooling-margin" || visual.type === "cooling-rate" || visual.type === "egt-balance" || visual.type === "pairing") {
      const statsUsed = drawVisualStats(visual.model?.stats, innerX, contentTop, innerWidth);
      const used = drawVisualTileGrid(visual.model?.tiles, innerX, contentTop - statsUsed, innerWidth, { columns: 4 });
      if (visual.model?.trace) {
        drawTraceVisual(visual, innerX, contentTop - statsUsed - used, innerWidth, {
          trace: visual.model.trace,
          color: visual.type === "cooling-rate" ? pdfPalette.purple
            : visual.type === "egt-balance" ? pdfPalette.blue
              : visual.type === "pairing" ? pdfPalette.red
                : pdfPalette.red,
          height: 68,
          stats: false,
        });
      }
    } else if (visual.type === "sensor-trust") {
      const statsUsed = drawVisualStats(visual.model?.stats, innerX, contentTop, innerWidth);
      drawVisualTileGrid(visual.model?.tiles, innerX, contentTop - statsUsed, innerWidth, { columns: 4 });
    } else if (visual.type === "electrical") {
      const statsUsed = drawVisualStats(visual.model?.stats, innerX, contentTop, innerWidth);
      const tileUsed = drawVisualTileGrid(visual.model?.tiles, innerX, contentTop - statsUsed, innerWidth, { columns: 4, limit: 4 });
      const traceTop = contentTop - statsUsed - tileUsed;
      if (visual.model?.trace) {
        drawTraceVisual(visual, innerX, traceTop, innerWidth, { trace: visual.model.trace, color: pdfPalette.blue, height: 50, stats: false });
      }
      if (visual.model?.startTrace) {
        drawTraceVisual(visual, innerX, traceTop - 76, innerWidth, { trace: visual.model.startTrace, color: pdfPalette.green, height: 48, stats: false });
      }
    }
    y -= 1;
  };
  const write = (text, options = {}) => {
    const size = options.size ?? 10;
    const leading = options.leading ?? size + 4;
    const x = margin + (options.indent ?? 0);
    const usableWidth = contentWidth - (options.indent ?? 0);
    const lines = wrap(text, size, usableWidth);
    lines.forEach((line, index) => {
      ensureSpace(leading);
      const shouldJustify = options.justify && !options.bold && index < lines.length - 1;
      textCommand(
        line,
        x,
        y,
        size,
        Boolean(options.bold),
        options.color ?? [0.08, 0.12, 0.18],
        { wordSpacing: shouldJustify ? justifiedWordSpacing(line, size, usableWidth) : 0 },
      );
      y -= leading;
    });
  };
  newPage();
  return {
    pages,
    cover({ title, subtitle, kicker, score, status }) {
      const bandHeight = 132;
      drawRect(0, height - bandHeight, width, bandHeight, { fill: pdfPalette.navy });
      drawRect(0, height - bandHeight, width, 6, { fill: pdfPalette.blue });
      textCommand(kicker ?? "Alary IQ advisory report", margin, height - 39, 9, true, [0.67, 0.78, 0.92]);
      textCommand(title, margin, height - 68, 24, true, pdfPalette.white);
      drawWrapped(subtitle, margin, height - 91, 10, 335, { color: [0.78, 0.85, 0.93], maxLines: 2 });
      if (Number.isFinite(score)) {
        const cardX = width - margin - 112;
        const cardY = height - 105;
        drawRect(cardX, cardY, 112, 62, { fill: pdfPalette.white });
        textCommand("OVERALL", cardX + 13, cardY + 43, 7.5, true, pdfPalette.muted);
        textCommand(`${Math.round(score)}`, cardX + 13, cardY + 18, 24, true, pdfColorForScore(score));
        textCommand("/100", cardX + 56, cardY + 20, 9, true, pdfPalette.muted);
        scoreBar(cardX + 13, cardY + 10, 86, score, { height: 4 });
      }
      if (status) drawWrapped(status, margin, height - 116, 8.5, 360, { color: [0.78, 0.85, 0.93], maxLines: 1 });
      y = height - bandHeight - 24;
    },
    title(text) {
      write(text, { size: 23, leading: 28, bold: true, color: [0.05, 0.11, 0.20] });
    },
    subtitle(text) {
      write(text, { size: 11, leading: 16, color: [0.36, 0.43, 0.52] });
    },
    heading(text) {
      ensureSpace(35);
      y -= 10;
      drawRect(margin, y - 3, 4, 17, { fill: pdfPalette.blue });
      textCommand(text.toUpperCase(), margin + 12, y, 11, true, pdfPalette.ink);
      y -= 21;
    },
    paragraph(text) {
      write(text, { size: 10, leading: 14, justify: true });
      y -= 3;
    },
    subheading(text) {
      write(text, { size: 10, leading: 14, bold: true });
    },
    keyValue(label, value) {
      write(`${label}: ${value}`, { size: 10, leading: 14 });
    },
    bullet(text) {
      write(`- ${text}`, { size: 10, leading: 14, indent: 10 });
    },
    keyValueGrid(rows, options = {}) {
      const columns = options.columns ?? 2;
      const gap = 16;
      const colWidth = (contentWidth - gap * (columns - 1)) / columns;
      const minimumRowHeight = options.rowHeight ?? 37;
      const valueSize = 9.5;
      const valueLeading = 11.5;
      const valueTopOffset = 24;
      const rowBottomPadding = 12;
      // Values keep every wrapped line: the row grows to fit and moves to the
      // next page when needed. Slicing to two lines silently dropped the tail
      // of real content (the Rotax profile guidance lost its second sentence).
      for (let index = 0; index < rows.length; index += columns) {
        const row = rows.slice(index, index + columns).map(([label, value]) => ({
          label,
          lines: wrap(value, valueSize, colWidth),
        }));
        const maxLines = Math.max(1, ...row.map((cell) => cell.lines.length));
        const rowHeight = Math.max(
          minimumRowHeight,
          valueTopOffset + (maxLines - 1) * valueLeading + rowBottomPadding,
        );
        ensureSpace(rowHeight + 2);
        const rowTop = y;
        row.forEach(({ label, lines }, offset) => {
          const x = margin + offset * (colWidth + gap);
          drawLine(x, rowTop - rowHeight + 8, x + colWidth, rowTop - rowHeight + 8, { color: pdfPalette.rule, width: 0.55 });
          textCommand(String(label).toUpperCase(), x, rowTop - 10, 7.5, true, pdfPalette.muted);
          lines.forEach((line, lineIndex) => {
            textCommand(line, x, rowTop - valueTopOffset - lineIndex * valueLeading, valueSize, false, pdfPalette.ink);
          });
        });
        y -= rowHeight;
      }
      y -= 4;
    },
    metricCards(cards, options = {}) {
      const columns = options.columns ?? 3;
      const gap = 10;
      const cardWidth = (contentWidth - gap * (columns - 1)) / columns;
      const cardHeight = options.height ?? 68;
      for (let index = 0; index < cards.length; index += columns) {
        const row = cards.slice(index, index + columns);
        const top = reserve(cardHeight + 10);
        row.forEach((card, offset) => {
          const x = margin + offset * (cardWidth + gap);
          const bottom = top - cardHeight;
          const fill = card.fill ?? (Number.isFinite(card.score) ? pdfSoftColorForScore(card.score) : pdfPalette.panel);
          drawRect(x, bottom, cardWidth, cardHeight, { fill, stroke: pdfPalette.rule, lineWidth: 0.55 });
          textCommand(String(card.label ?? "").toUpperCase(), x + 10, top - 14, 7.2, true, pdfPalette.muted);
          textCommand(pdfTruncateText(card.value ?? "", 17, cardWidth - 20), x + 10, top - 36, 17, true, card.color ?? pdfColorForScore(card.score));
          if (card.detail) {
            drawWrapped(card.detail, x + 10, top - 51, 7.8, cardWidth - 20, { color: pdfPalette.muted, maxLines: 1 });
          }
          if (Number.isFinite(card.score)) scoreBar(x + 10, bottom + 9, cardWidth - 20, card.score, { height: 4 });
        });
      }
    },
    scoreBars(rows, options = {}) {
      const rowHeight = options.rowHeight ?? 30;
      const labelWidth = options.labelWidth ?? 116;
      const scoreWidth = 46;
      const barWidth = options.barWidth ?? 98;
      rows.forEach((row) => {
        ensureSpace(rowHeight + 2);
        const top = y;
        drawLine(margin, top - rowHeight + 6, width - margin, top - rowHeight + 6, { color: pdfPalette.rule, width: 0.45 });
        textCommand(pdfTruncateText(row.label, 9.5, labelWidth), margin, top - 12, 9.5, true, pdfPalette.ink);
        const scoreText = Number.isFinite(row.score) ? `${Math.round(row.score)}/100` : "--";
        textCommand(scoreText, margin + labelWidth, top - 12, 9.5, true, pdfColorForScore(row.score));
        scoreBar(margin + labelWidth + scoreWidth, top - 17, barWidth, row.score, { height: 5 });
        const noteX = margin + labelWidth + scoreWidth + barWidth + 14;
        textCommand(pdfTruncateText(row.note ?? "", 8.5, width - margin - noteX), noteX, top - 12, 8.5, false, pdfPalette.muted);
        y -= rowHeight;
      });
      y -= 5;
    },
    turboContext(context) {
      if (!context) return;
      const tiles = (context.tiles ?? []).filter(Boolean).slice(0, 4);
      const innerX = margin + 14;
      const innerWidth = contentWidth - 28;
      const tileGap = 8;
      const tileColumns = 4;
      const tileWidth = (innerWidth - tileGap * (tileColumns - 1)) / tileColumns;
      const tileHeight = 48;
      const noteHeight = context.note
        ? 26 + wrappedHeight(context.note, 8.4, innerWidth - 18, { leading: 11.2, maxLines: 3 }) + 8
        : 0;
      const reviewHeight = context.review
        ? 25 + wrappedHeight(context.review, 8.4, innerWidth - 18, { leading: 11.2, maxLines: 3 }) + 8
        : 0;
      const panelHeight = 56 + tileHeight + 10 + noteHeight + (reviewHeight ? reviewHeight + 8 : 0) + 12;
      const top = reserve(panelHeight + 8);
      const bottom = top - panelHeight;
      const accent = pdfColorForScore(context.score);
      drawRect(margin, bottom, contentWidth, panelHeight, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.55 });
      drawRect(margin, bottom, 5, panelHeight, { fill: accent });
      textCommand("TURBO / BOOST CONTEXT", innerX, top - 16, 7.4, true, pdfPalette.muted);
      drawWrapped(context.status ?? "Turbo context", innerX, top - 34, 12.8, innerWidth - 128, { bold: true, leading: 14.5, maxLines: 2 });
      if (context.subtitle) {
        drawWrapped(context.subtitle, innerX, top - 49, 8.1, innerWidth - 128, { color: pdfPalette.muted, maxLines: 1 });
      }
      const badgeScoreText = Number.isFinite(context.score) ? `${Math.round(context.score)}/100` : "--";
      const badgeLabelText = String(context.badge ?? "Context");
      const badgeScoreWidth = estimatedTextWidth(badgeScoreText, 12);
      const badgePadding = 10;
      const badgeGap = 8;
      const badgeLabelX = badgePadding + badgeScoreWidth + badgeGap;
      const badgeMinWidth = 91;
      const badgeWidth = Math.max(badgeMinWidth, Math.ceil(badgeLabelX + estimatedTextWidth(badgeLabelText, 7.2) + badgePadding));
      const badgeX = width - margin - 14 - badgeWidth;
      drawRect(badgeX, top - 40, badgeWidth, 25, { fill: pdfSoftColorForScore(context.score), stroke: pdfPalette.rule, lineWidth: 0.35 });
      textCommand(badgeScoreText, badgeX + badgePadding, top - 29, 12, true, accent);
      textCommand(pdfTruncateText(badgeLabelText, 7.2, badgeWidth - badgeLabelX - badgePadding), badgeX + badgeLabelX, top - 29, 7.2, true, pdfPalette.muted);
      if (Number.isFinite(context.score)) scoreBar(badgeX + badgePadding, top - 36, Math.max(42, badgeWidth - badgePadding * 2 - 2), context.score, { height: 3.2 });

      let rowTop = top - 58;
      tiles.forEach((tile, index) => {
        const x = innerX + index * (tileWidth + tileGap);
        const tileBottom = rowTop - tileHeight;
        drawRect(x, tileBottom, tileWidth, tileHeight, { fill: pdfPalette.panel, stroke: pdfPalette.rule, lineWidth: 0.35 });
        textCommand(String(tile.label ?? "").toUpperCase(), x + 8, rowTop - 12, 6.8, true, pdfPalette.muted);
        textCommand(pdfTruncateText(tile.value ?? "n/a", 12.4, tileWidth - 16), x + 8, rowTop - 30, 12.4, true, tile.color ?? pdfPalette.ink);
        if (tile.detail) {
          drawWrapped(tile.detail, x + 8, rowTop - 41, 7.1, tileWidth - 16, { color: pdfPalette.muted, leading: 8.8, maxLines: 1 });
        }
      });
      rowTop -= tileHeight + 10;

      if (context.note) {
        const noteTop = rowTop;
        const noteBottom = noteTop - noteHeight;
        drawRect(innerX, noteBottom, innerWidth, noteHeight, { fill: [0.985, 0.99, 1.00], stroke: pdfPalette.rule, lineWidth: 0.3 });
        textCommand("Context", innerX + 9, noteTop - 14, 8, true, pdfPalette.ink);
        drawWrapped(context.note, innerX + 9, noteTop - 29, 8.4, innerWidth - 18, { leading: 11.2, maxLines: 3, color: pdfPalette.ink, justify: true });
        rowTop = noteBottom - 8;
      }

      if (context.review) {
        const reviewTop = rowTop;
        const reviewBottom = reviewTop - reviewHeight;
        drawRect(innerX, reviewBottom, innerWidth, reviewHeight, { fill: [0.96, 0.98, 1.00], stroke: pdfPalette.rule, lineWidth: 0.3 });
        textCommand("Review", innerX + 9, reviewTop - 14, 8, true, pdfPalette.ink);
        drawWrapped(context.review, innerX + 9, reviewTop - 29, 8.4, innerWidth - 18, { leading: 11.2, maxLines: 3, color: pdfPalette.ink, justify: true });
      }
      y -= 1;
    },
    triageSummary({ status, title, score, scoreLabel, evidence, evidenceItems = [], body, lesson, confidence, actions = [] }) {
      const statusColor = pdfColorForScore(score);
      const summaryHeight = 86;
      const top = reserve(summaryHeight + 8);
      const bottom = top - summaryHeight;
      drawRect(margin, bottom, contentWidth, summaryHeight, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.55 });
      drawRect(margin, bottom, 5, summaryHeight, { fill: statusColor });
      textCommand("PRIMARY ENGINE CUE", margin + 16, top - 16, 7.5, true, pdfPalette.muted);
      drawWrapped(title, margin + 16, top - 35, 14, contentWidth - 170, { bold: true, leading: 16, maxLines: 2 });
      drawRect(width - margin - 128, top - 36, 102, 18, { fill: pdfSoftColorForScore(score), stroke: pdfPalette.rule, lineWidth: 0.35 });
      textCommand(status, width - margin - 116, top - 30, 8.5, true, statusColor);
      textCommand(Number.isFinite(score) ? `${Math.round(score)}/100` : "--", width - margin - 116, bottom + 22, 18, true, statusColor);
      textCommand(scoreLabel ?? "Score", width - margin - 60, bottom + 25, 8.2, false, pdfPalette.muted);
      scoreBar(width - margin - 116, bottom + 12, 88, score, { height: 4 });

      const chips = (evidenceItems.length ? evidenceItems : [evidence]).filter(Boolean).slice(0, 8);
      if (chips.length) {
        const chipGap = 6;
        const chipHeight = 18;
        const chipRows = [];
        let currentRow = [];
        let currentWidth = 0;
        chips.forEach((chip) => {
          const chipWidth = Math.min(contentWidth - 28, Math.max(76, pdfSafeText(chip).length * 4.2 + 20));
          if (currentRow.length && currentWidth + chipGap + chipWidth > contentWidth - 28) {
            chipRows.push(currentRow);
            currentRow = [];
            currentWidth = 0;
          }
          currentRow.push({ text: chip, width: chipWidth });
          currentWidth += (currentRow.length > 1 ? chipGap : 0) + chipWidth;
        });
        if (currentRow.length) chipRows.push(currentRow);
        const evidenceHeight = 27 + chipRows.length * (chipHeight + 6) + 6;
        const evidenceTop = reserve(evidenceHeight + 8);
        const evidenceBottom = evidenceTop - evidenceHeight;
        drawRect(margin, evidenceBottom, contentWidth, evidenceHeight, { fill: pdfPalette.panel, stroke: pdfPalette.rule, lineWidth: 0.45 });
        textCommand("Evidence", margin + 14, evidenceTop - 16, 9.2, true, pdfPalette.ink);
        let chipY = evidenceTop - 39;
        chipRows.forEach((row) => {
          let chipX = margin + 14;
          row.forEach((chip) => {
            drawRect(chipX, chipY, chip.width, chipHeight, { fill: pdfPalette.white, stroke: [0.78, 0.84, 0.91], lineWidth: 0.35 });
            textCommand(pdfTruncateText(chip.text, 7.4, chip.width - 14), chipX + 7, chipY + 6, 7.4, true, pdfPalette.muted);
            chipX += chip.width + chipGap;
          });
          chipY -= chipHeight + 6;
        });
      }

      if (body) {
        const bodyWidth = contentWidth - 28;
        const bodyHeight = 29 + wrappedHeight(body, 9.2, bodyWidth, { leading: 12.8 }) + 13;
        const bodyTop = reserve(bodyHeight + 8);
        const bodyBottom = bodyTop - bodyHeight;
        drawRect(margin, bodyBottom, contentWidth, bodyHeight, { fill: [0.985, 0.99, 1.00], stroke: pdfPalette.rule, lineWidth: 0.45 });
        textCommand("Interpretation", margin + 14, bodyTop - 17, 9.2, true, pdfPalette.ink);
        drawWrapped(body, margin + 14, bodyTop - 34, 9.2, bodyWidth, { leading: 12.8, color: pdfPalette.ink, justify: true });
      }

      const lessonBlocks = [
        ["What It Watches", lesson?.watches],
        ["What It Means", lesson?.meaning],
        ["How To Review It", lesson?.review],
      ].filter(([, value]) => value);
      lessonBlocks.forEach(([label, value]) => {
        const blockWidth = contentWidth - 28;
        const blockHeight = 25 + wrappedHeight(value, 8.6, blockWidth, { leading: 11.7 }) + 10;
        const blockTop = reserve(blockHeight + 6);
        const blockBottom = blockTop - blockHeight;
        drawRect(margin, blockBottom, contentWidth, blockHeight, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.35 });
        textCommand(label, margin + 14, blockTop - 15, 8.2, true, pdfPalette.muted);
        drawWrapped(value, margin + 14, blockTop - 30, 8.6, blockWidth, { leading: 11.7, color: pdfPalette.ink, justify: true });
      });

      const actionRows = actions.slice(0, 4);
      const confidenceText = confidence ? `Confidence: ${confidence}` : "";
      const actionHeights = actionRows.map(([, action]) => Math.max(15, wrappedHeight(action, 8.5, contentWidth - 98, { leading: 11.3 })));
      const actionsHeight = 28 + (confidenceText ? 15 : 0) + actionHeights.reduce((sum, value) => sum + value + 7, 0) + 8;
      const actionsTop = reserve(actionsHeight + 2);
      const actionsBottom = actionsTop - actionsHeight;
      drawRect(margin, actionsBottom, contentWidth, actionsHeight, { fill: [0.96, 0.98, 1.00], stroke: pdfPalette.rule, lineWidth: 0.45 });
      textCommand("Next Review Steps", margin + 14, actionsTop - 17, 9.2, true, pdfPalette.ink);
      let rowY = actionsTop - 34;
      if (confidenceText) {
        textCommand(confidenceText, margin + 14, rowY, 8.4, true, pdfPalette.muted);
        rowY -= 15;
      }
      actionRows.forEach(([priority, action], index) => {
        const priorityColor = priority === "high" ? pdfPalette.red : priority === "medium" ? pdfPalette.amber : pdfPalette.muted;
        const actionHeight = actionHeights[index];
        drawRect(margin + 14, rowY - 10, 48, 14, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.35 });
        textCommand(titleCase(priority), margin + 20, rowY - 5, 7.2, true, priorityColor);
        drawWrapped(action, margin + 76, rowY, 8.5, contentWidth - 98, { leading: 11.3, color: pdfPalette.ink });
        rowY -= actionHeight + 7;
      });
      y -= 4;
    },
    conceptCards(items) {
      items.forEach((item) => {
        const cardWidth = contentWidth;
        const innerX = margin + 14;
        const labelWidth = 82;
        const textX = innerX + labelWidth + 12;
        const textWidth = cardWidth - labelWidth - 40;
        const titleText = item.title ?? item.label ?? "Engine concept";
        const cueText = item.note ? `Report note: ${item.note}` : "";
        const titleHeight = Math.max(15, wrappedHeight(titleText, 10.6, cardWidth - 150, { leading: 13.4, maxLines: 2 }));
        const cueHeight = cueText ? Math.max(12, wrappedHeight(cueText, 8.2, cardWidth - 28, { leading: 10.8, maxLines: 2 })) + 4 : 0;
        const rows = [
          ["Watches", item.watches],
          ["Means", item.meaning],
          ["Review", item.review],
        ].filter(([, value]) => value);
        const rowHeights = rows.map(([, value]) => Math.max(24, wrappedHeight(value, 8.3, textWidth, { leading: 11.1 }) + 12));
        const cardHeight = 26 + titleHeight + cueHeight + rowHeights.reduce((sum, value) => sum + value, 0) + 14;
        const top = reserve(cardHeight + 10);
        const bottom = top - cardHeight;
        const accent = pdfColorForScore(item.score);
        drawRect(margin, bottom, cardWidth, cardHeight, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.5 });
        drawRect(margin, bottom, 4, cardHeight, { fill: accent });
        textCommand("ENGINE CONCEPT", innerX, top - 14, 7.1, true, pdfPalette.muted);
        drawWrapped(titleText, innerX, top - 31, 10.6, cardWidth - 150, { bold: true, leading: 13.4, maxLines: 2 });
        if (Number.isFinite(item.score)) {
          textCommand(`${Math.round(item.score)}/100`, width - margin - 86, top - 29, 10.8, true, accent);
          scoreBar(width - margin - 86, top - 39, 72, item.score, { height: 3.5 });
        }

        let rowTop = top - 30 - titleHeight;
        if (cueText) {
          drawRect(innerX, rowTop - cueHeight + 2, cardWidth - 28, cueHeight, { fill: pdfPalette.panel, stroke: pdfPalette.rule, lineWidth: 0.25 });
          drawWrapped(cueText, innerX + 8, rowTop - 10, 8.2, cardWidth - 44, { leading: 10.8, color: pdfPalette.muted, maxLines: 2 });
          rowTop -= cueHeight + 6;
        } else {
          rowTop -= 5;
        }

        rows.forEach(([label, value], index) => {
          const rowHeight = rowHeights[index];
          const rowBottom = rowTop - rowHeight;
          if (index % 2 === 0) drawRect(innerX, rowBottom, cardWidth - 28, rowHeight, { fill: [0.988, 0.992, 1.0] });
          drawLine(innerX, rowBottom, margin + cardWidth - 14, rowBottom, { color: pdfPalette.rule, width: 0.3 });
          textCommand(label.toUpperCase(), innerX + 8, rowTop - 14, 7.2, true, pdfPalette.muted);
          drawWrapped(value, textX, rowTop - 12, 8.3, textWidth, { leading: 11.1, color: pdfPalette.ink, justify: true });
          rowTop -= rowHeight;
        });
        y -= 2;
      });
    },
    reviewCueCards(items) {
      items.forEach((item) => {
        const cardWidth = contentWidth;
        const innerX = margin + 14;
        const innerWidth = cardWidth - 28;
        const titleWidth = cardWidth - 148;
        const accent = pdfColorForScore(item.score);
        const titleText = item.title ?? item.label ?? "Review";
        const titleHeight = Math.max(13, wrappedHeight(titleText, 10.8, titleWidth, { leading: 13.2, maxLines: 2 }));
        const evidenceItems = (item.evidenceItems ?? []).filter(Boolean).slice(0, 4);
        const chipGap = 6;
        const chipHeight = 17;
        const chipRowWidth = innerWidth - 16;
        const chipRows = [];
        let currentRow = [];
        let currentWidth = 0;
        evidenceItems.forEach((chip) => {
          const chipWidth = Math.min(chipRowWidth, Math.max(82, pdfSafeText(chip).length * 3.7 + 18));
          if (currentRow.length && currentWidth + chipGap + chipWidth > chipRowWidth) {
            chipRows.push(currentRow);
            currentRow = [];
            currentWidth = 0;
          }
          currentRow.push({ text: chip, width: chipWidth });
          currentWidth += (currentRow.length > 1 ? chipGap : 0) + chipWidth;
        });
        if (currentRow.length) chipRows.push(currentRow);

        const evidenceHeight = chipRows.length ? 28 + chipRows.length * (chipHeight + 5) : 0;
        const windowHeight = item.window && item.window !== "No marked window" ? 24 : 0;
        const bodyHeight = item.body ? 7 + wrappedHeight(item.body, 8.6, innerWidth, { leading: 11.6, maxLines: 4 }) : 0;
        const causeRows = (item.causes ?? []).slice(0, 3);
        const showCauseWeights = scoreClass(item.score) !== "green";
        const causeTextWidth = showCauseWeights ? innerWidth - 74 : innerWidth - 30;
        const causeHeights = causeRows.map(([cause]) => Math.max(12, wrappedHeight(cause, 7.8, causeTextWidth, { leading: 10.2, maxLines: 2 })));
        const causesHeight = causeRows.length ? 23 + causeHeights.reduce((sum, value) => sum + value + 5, 0) : 0;
        const actionRows = (item.actions ?? []).slice(0, 2);
        const actionTextWidth = innerWidth - 74;
        const actionHeights = actionRows.map(([, action]) => Math.max(13, wrappedHeight(action, 8.1, actionTextWidth, { leading: 10.6, maxLines: 2 })));
        const actionsHeight = actionRows.length ? 24 + actionHeights.reduce((sum, value) => sum + value + 6, 0) : 0;
        const cardTopPadding = 30;
        const cardBottomPadding = 10;
        const sectionGap = 7;
        const bodyGap = 5;
        const cardHeight = cardTopPadding
          + titleHeight
          + (evidenceHeight ? evidenceHeight + sectionGap : 0)
          + (windowHeight ? windowHeight + sectionGap : 0)
          + (bodyHeight ? bodyHeight + bodyGap : 0)
          + (causesHeight ? causesHeight + sectionGap : 0)
          + actionsHeight
          + cardBottomPadding;
        const top = reserve(cardHeight + 10);
        const bottom = top - cardHeight;

        drawRect(margin, bottom, cardWidth, cardHeight, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.5 });
        drawRect(margin, bottom, 5, cardHeight, { fill: accent });
        textCommand(String(item.label ?? "Review").toUpperCase(), innerX, top - 14, 7.2, true, pdfPalette.muted);
        drawWrapped(titleText, innerX, top - 31, 10.8, titleWidth, { bold: true, leading: 13.2, maxLines: 2 });
        if (Number.isFinite(item.score)) {
          textCommand(`${Math.round(item.score)}/100`, width - margin - 89, top - 29, 11, true, accent);
          scoreBar(width - margin - 89, top - 39, 75, item.score, { height: 3.5 });
        }

        let sectionTop = top - 30 - titleHeight;
        if (chipRows.length) {
          const panelTop = sectionTop;
          const panelBottom = panelTop - evidenceHeight;
          drawRect(innerX, panelBottom, innerWidth, evidenceHeight, { fill: pdfPalette.panel, stroke: pdfPalette.rule, lineWidth: 0.35 });
          textCommand("Evidence", innerX + 8, panelTop - 13, 8.1, true, pdfPalette.ink);
          let chipY = panelTop - 35;
          chipRows.forEach((row) => {
            let chipX = innerX + 8;
            row.forEach((chip) => {
              drawRect(chipX, chipY, chip.width, chipHeight, { fill: pdfPalette.white, stroke: [0.78, 0.84, 0.91], lineWidth: 0.3 });
              textCommand(pdfTruncateText(chip.text, 7.2, chip.width - 12), chipX + 6, chipY + 5.5, 7.2, true, pdfPalette.muted);
              chipX += chip.width + chipGap;
            });
            chipY -= chipHeight + 5;
          });
          sectionTop = panelBottom - 7;
        }

        if (windowHeight) {
          drawRect(innerX, sectionTop - windowHeight, innerWidth, windowHeight, { fill: [0.985, 0.99, 1.00], stroke: pdfPalette.rule, lineWidth: 0.25 });
          textCommand("WINDOW", innerX + 8, sectionTop - 15, 7.3, true, pdfPalette.muted);
          textCommand(pdfTruncateText(item.window, 8.1, innerWidth - 78), innerX + 68, sectionTop - 15, 8.1, false, pdfPalette.ink);
          sectionTop -= windowHeight + 7;
        }

        if (item.body) {
          drawWrapped(item.body, innerX, sectionTop - 2, 8.6, innerWidth, { leading: 11.6, maxLines: 4, color: pdfPalette.ink, justify: true });
          sectionTop -= bodyHeight + 5;
        }

        if (causeRows.length) {
          const causesTop = sectionTop;
          const causesBottom = causesTop - causesHeight;
          drawRect(innerX, causesBottom, innerWidth, causesHeight, { fill: [1.0, 0.985, 0.95], stroke: pdfPalette.rule, lineWidth: 0.3 });
          textCommand(showCauseWeights ? "Possible causes" : "Review notes", innerX + 8, causesTop - 13, 8, true, pdfPalette.ink);
          let causeY = causesTop - 30;
          causeRows.forEach(([cause, probability], index) => {
            const causeHeight = causeHeights[index];
            if (showCauseWeights) {
              drawRect(innerX + 8, causeY - 10, 48, 13, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.25 });
              textCommand(`${Math.round((probability ?? 0) * 100)}%`, innerX + 16, causeY - 5, 6.9, true, pdfPalette.amber);
              drawWrapped(cause, innerX + 68, causeY, 7.8, causeTextWidth, { leading: 10.2, maxLines: 2, color: pdfPalette.ink });
            } else {
              textCommand("-", innerX + 10, causeY, 8.5, true, pdfPalette.muted);
              drawWrapped(cause, innerX + 24, causeY, 7.8, causeTextWidth, { leading: 10.2, maxLines: 2, color: pdfPalette.ink });
            }
            causeY -= causeHeight + 5;
          });
          sectionTop = causesBottom - 7;
        }

        if (actionRows.length) {
          const actionsTop = sectionTop;
          const actionsBottom = actionsTop - actionsHeight;
          drawRect(innerX, actionsBottom, innerWidth, actionsHeight, { fill: [0.96, 0.98, 1.00], stroke: pdfPalette.rule, lineWidth: 0.3 });
          textCommand("Suggested review", innerX + 8, actionsTop - 14, 8, true, pdfPalette.ink);
          let actionY = actionsTop - 31;
          actionRows.forEach(([priority, action], index) => {
            const priorityColor = priority === "high" ? pdfPalette.red : priority === "medium" ? pdfPalette.amber : pdfPalette.muted;
            const actionHeight = actionHeights[index];
            drawRect(innerX + 8, actionY - 10, 48, 14, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.25 });
            textCommand(titleCase(priority), innerX + 14, actionY - 5, 7, true, priorityColor);
            drawWrapped(action, innerX + 68, actionY, 8.1, actionTextWidth, { leading: 10.6, maxLines: 2, color: pdfPalette.ink });
            actionY -= actionHeight + 6;
          });
        }
        y -= 1;
      });
    },
    simpleTable(headers, rows, widths, options = {}) {
      const rowHeight = options.rowHeight ?? 24;
      // Column whose numeric value is tinted by score color. Defaults to 1 for
      // the score tables; pass a negative index for plain (non-score) tables.
      const scoreColumn = options.scoreColumn ?? 1;
      const boldColumns = options.boldColumns ?? 2;
      const headerHeight = 22;
      const tableWidth = widths.reduce((total, value) => total + value, 0);
      ensureSpace(headerHeight + rowHeight + 4);
      let top = y;
      drawRect(margin, top - headerHeight, tableWidth, headerHeight, { fill: pdfPalette.panelAlt, stroke: pdfPalette.rule, lineWidth: 0.55 });
      let x = margin;
      headers.forEach((header, index) => {
        textCommand(pdfTruncateText(header, 7.5, widths[index] - 8), x + 5, top - 14, 7.5, true, pdfPalette.muted);
        x += widths[index];
      });
      y -= headerHeight;
      rows.forEach((row, rowIndex) => {
        ensureSpace(rowHeight + 2);
        top = y;
        if (rowIndex % 2 === 1) drawRect(margin, top - rowHeight, tableWidth, rowHeight, { fill: [0.985, 0.99, 1.00] });
        drawLine(margin, top - rowHeight, margin + tableWidth, top - rowHeight, { color: pdfPalette.rule, width: 0.35 });
        x = margin;
        row.forEach((cell, index) => {
          const color = index === scoreColumn ? pdfColorForScore(Number(String(cell).match(/\d+/)?.[0])) : pdfPalette.ink;
          textCommand(pdfTruncateText(cell, 8.2, widths[index] - 8), x + 5, top - 15, 8.2, index < boldColumns, color);
          x += widths[index];
        });
        y -= rowHeight;
      });
      y -= 8;
    },
    sparklineRows(title, rows) {
      const visibleRows = rows.filter((row) => row.values?.some(Number.isFinite));
      if (!visibleRows.length) return;
      const headerHeight = 30;
      const rowHeight = 34;
      const panelHeight = headerHeight + visibleRows.length * rowHeight + 14;
      const top = reserve(panelHeight + 8);
      const bottom = top - panelHeight;
      drawRect(margin, bottom, contentWidth, panelHeight, { fill: pdfPalette.panel, stroke: pdfPalette.rule, lineWidth: 0.55 });
      textCommand(title, margin + 14, top - 19, 10.5, true, pdfPalette.ink);
      let rowTop = top - headerHeight;
      visibleRows.forEach((row) => {
        const rowBottom = rowTop - rowHeight;
        const labelX = margin + 14;
        const sparkX = margin + 102;
        const sparkWidth = contentWidth - 220;
        const sparkBottom = rowBottom + 9;
        const sparkHeight = 16;
        const finite = row.values.filter(Number.isFinite);
        const extent = extentFromValues(finite);
        textCommand(row.label, labelX, rowTop - 15, 8.5, true, pdfPalette.ink);
        drawLine(sparkX, sparkBottom, sparkX + sparkWidth, sparkBottom, { color: [0.86, 0.89, 0.92], width: 0.4 });
        drawLine(sparkX, sparkBottom + sparkHeight, sparkX + sparkWidth, sparkBottom + sparkHeight, { color: [0.90, 0.93, 0.96], width: 0.35 });
        if (!sparklinePath(row.values, sparkX, sparkBottom, sparkWidth, sparkHeight, row.color ?? pdfPalette.blue)) {
          textCommand("No trace", sparkX, rowTop - 15, 8, false, pdfPalette.muted);
        }
        const digits = row.digits ?? 0;
        const minLabel = Number.isFinite(extent?.min) ? `${Number(extent.min).toFixed(digits)}${row.suffix ?? ""}` : "n/a";
        const maxLabel = Number.isFinite(extent?.max) ? `${Number(extent.max).toFixed(digits)}${row.suffix ?? ""}` : "n/a";
        textCommand(`${minLabel} - ${maxLabel}`, sparkX + sparkWidth + 14, rowTop - 11, 7.7, true, pdfPalette.muted);
        textCommand(pdfTruncateText(row.summary ?? "", 7.5, 92), sparkX + sparkWidth + 14, rowTop - 23, 7.5, false, pdfPalette.muted);
        rowTop -= rowHeight;
      });
    },
    phaseTimeline(phases) {
      const buckets = profileDurationBuckets(phases);
      if (!buckets.length) return;
      const panelHeight = 64;
      const top = reserve(panelHeight + 8);
      const bottom = top - panelHeight;
      const palette = {
        ground: pdfPalette.amber,
        airborne: pdfPalette.blue,
        off: pdfPalette.muted,
        unknown: pdfPalette.purple,
      };
      const total = buckets.reduce((sum, bucket) => sum + bucket.durationSeconds, 0) || 1;
      const barX = margin + 14;
      const barY = bottom + 18;
      const barWidth = contentWidth - 28;
      drawRect(margin, bottom, contentWidth, panelHeight, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.55 });
      textCommand("Operational Mix", margin + 14, top - 17, 10.2, true, pdfPalette.ink);
      let x = barX;
      buckets.forEach((bucket) => {
        const segmentWidth = barWidth * bucket.durationSeconds / total;
        drawRect(x, barY, segmentWidth, 14, { fill: palette[bucket.key] ?? pdfPalette.teal });
        if (segmentWidth > 50) {
          textCommand(pdfTruncateText(bucket.label, 7, segmentWidth - 8), x + 4, barY + 4, 7, true, pdfPalette.white);
        }
        x += segmentWidth;
      });
      const summary = buckets.map((bucket) => `${bucket.label} ${formatElapsedTime(bucket.durationSeconds)}`).join(" | ");
      textCommand(pdfTruncateText(summary, 7.8, contentWidth - 28), margin + 14, bottom + 8, 7.8, false, pdfPalette.muted);
    },
    flightMap(image, caption, tiles = []) {
      if (!image?.dataUrl) return;
      const visibleTiles = tiles.filter((tile) => tile?.[0]).slice(0, 16);
      const tileColumns = 4;
      const tileGap = 7;
      const tileWidth = (contentWidth - 28 - tileGap * (tileColumns - 1)) / tileColumns;
      const tileHeight = 38;
      const tileRows = Math.ceil(visibleTiles.length / tileColumns);
      const tileGridHeight = tileRows ? tileRows * tileHeight + Math.max(0, tileRows - 1) * tileGap + 12 : 0;
      const panelHeight = 292 + tileGridHeight;
      const headerHeight = 32;
      const imageHeight = 220;
      const top = reserve(panelHeight + 10);
      const bottom = top - panelHeight;
      drawRect(margin, bottom, contentWidth, panelHeight, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.55 });
      textCommand("Flight Map", margin + 14, top - 19, 10.5, true, pdfPalette.ink);
      drawImage(image, margin + 14, top - headerHeight - imageHeight, contentWidth - 28, imageHeight);
      const captionY = top - headerHeight - imageHeight - 14;
      textCommand(pdfTruncateText(caption ?? "OpenStreetMap flight track", 7.6, contentWidth - 28), margin + 14, captionY, 7.6, false, pdfPalette.muted);
      let tileTop = captionY - 12;
      visibleTiles.forEach(([label, value], index) => {
        const column = index % tileColumns;
        const row = Math.floor(index / tileColumns);
        const x = margin + 14 + column * (tileWidth + tileGap);
        const cardTop = tileTop - row * (tileHeight + tileGap);
        const cardBottom = cardTop - tileHeight;
        drawRect(x, cardBottom, tileWidth, tileHeight, { fill: pdfPalette.panel, stroke: pdfPalette.rule, lineWidth: 0.35 });
        textCommand(pdfTruncateText(label, 6.8, tileWidth - 12), x + 7, cardTop - 12, 6.8, true, pdfPalette.muted);
        textCommand(pdfTruncateText(value, 11.2, tileWidth - 12), x + 7, cardTop - 28, 11.2, true, pdfPalette.ink);
      });
    },
    overviewVisuals(visuals = []) {
      const available = (Array.isArray(visuals) ? visuals : []).filter((visual) => visual?.available !== false);
      if (!available.length) return;
      this.heading("Overview Visuals");
      available.forEach((visual) => drawOverviewVisualPanel(visual));
    },
    gap(space = 8) {
      ensureSpace(space);
      y -= space;
    },
    ensureSpace(space) {
      ensureSpace(space);
    },
    headlineSummary({ statusLabel, statusClassName, score, title, paragraph, actions = [] }) {
      const accent = statusClassName === "red" ? pdfPalette.red
        : statusClassName === "amber" ? pdfPalette.amber
        : statusClassName === "green" ? pdfPalette.green
        : pdfPalette.quiet;
      const softAccent = statusClassName === "red" ? [1.00, 0.94, 0.93]
        : statusClassName === "amber" ? [1.00, 0.97, 0.89]
        : statusClassName === "green" ? [0.93, 0.98, 0.95]
        : pdfPalette.panel;
      const innerX = margin + 18;
      const innerWidth = contentWidth - 36;
      const badgeWidth = statusLabel ? Math.max(74, statusLabel.length * 5.6 + 24) : 0;
      const titleWidth = innerWidth - (badgeWidth ? badgeWidth + 20 : 0);
      const titleSize = 17;
      const titleLeading = 20;
      const titleHeight = Math.max(titleLeading, wrappedHeight(title ?? "Engine review", titleSize, titleWidth, { leading: titleLeading, maxLines: 2 }));
      const paragraphSize = 10;
      const paragraphLeading = 13.6;
      const paragraphHeight = paragraph ? wrappedHeight(paragraph, paragraphSize, innerWidth, { leading: paragraphLeading, maxLines: 4 }) + 8 : 0;
      const actionRows = (actions ?? []).slice(0, 2);
      const actionTextWidth = innerWidth - 22;
      const actionHeights = actionRows.map(([, action]) => Math.max(13, wrappedHeight(action, 9, actionTextWidth, { leading: 12, maxLines: 2 })));
      const actionsHeight = actionRows.length ? 22 + actionHeights.reduce((sum, value) => sum + value + 6, 0) + 2 : 0;
      const cardHeight = 32 + titleHeight + paragraphHeight + actionsHeight + 16;
      const top = reserve(cardHeight + 10);
      const bottom = top - cardHeight;
      drawRect(margin, bottom, contentWidth, cardHeight, { fill: softAccent, stroke: pdfPalette.rule, lineWidth: 0.55 });
      drawRect(margin, bottom, 6, cardHeight, { fill: accent });
      textCommand("TOP FINDING", innerX, top - 16, 7.5, true, pdfPalette.muted);
      if (statusLabel) {
        const badgeX = width - margin - badgeWidth - 14;
        drawRect(badgeX, top - 26, badgeWidth, 18, { fill: pdfPalette.white, stroke: accent, lineWidth: 0.6 });
        textCommand(String(statusLabel).toUpperCase(), badgeX + 10, top - 19, 8.4, true, accent);
      }
      drawWrapped(title ?? "Engine review", innerX, top - 38, titleSize, titleWidth, { bold: true, leading: titleLeading, maxLines: 2 });

      let sectionTop = top - 32 - titleHeight - 4;
      if (paragraph) {
        drawWrapped(paragraph, innerX, sectionTop, paragraphSize, innerWidth, { leading: paragraphLeading, maxLines: 4, color: pdfPalette.ink, justify: true });
        sectionTop -= paragraphHeight;
      }
      if (actionRows.length) {
        textCommand("WHAT TO CHECK NEXT", innerX, sectionTop - 4, 7.5, true, pdfPalette.muted);
        let rowY = sectionTop - 20;
        actionRows.forEach(([priority, action], index) => {
          const priorityColor = priority === "high" ? pdfPalette.red
            : priority === "medium" ? pdfPalette.amber
            : pdfPalette.muted;
          drawDot(innerX + 4, rowY + 3, 2.6, priorityColor);
          drawWrapped(action, innerX + 16, rowY, 9, actionTextWidth, { leading: 12, maxLines: 2, color: pdfPalette.ink });
          rowY -= actionHeights[index] + 6;
        });
      }
      y -= 6;
    },
    findingCard({ label, title, score, statusLabel, whatWeSaw, contextLine, whyItMatters, causes = [], causesLabel = "Why this might be happening", showCauseWeights = true, actions = [], confidence }) {
      const cardWidth = contentWidth;
      const innerX = margin + 16;
      const innerWidth = cardWidth - 32;
      const accent = pdfColorForScore(score);
      const titleText = title ?? label ?? "Finding";
      const titleSize = 11.6;
      const titleLeading = 14;
      const titleWidth = cardWidth - 160;
      const titleHeight = Math.max(titleLeading, wrappedHeight(titleText, titleSize, titleWidth, { leading: titleLeading, maxLines: 2 }));
      const sectionInsetX = innerX - 8;
      const sectionWidth = innerWidth + 16;
      const bodyWidth = innerWidth;
      const whatHeight = whatWeSaw
        ? 22 + wrappedHeight(whatWeSaw, 9.4, bodyWidth, { leading: 12.4, maxLines: 4 }) + (contextLine ? 14 : 0) + 10
        : 0;
      const whyHeight = whyItMatters
        ? 22 + wrappedHeight(whyItMatters, 9.2, bodyWidth, { leading: 12.2, maxLines: 4 }) + 10
        : 0;
      const causeRows = (causes ?? []).slice(0, 3);
      const causeTextWidth = bodyWidth - 22;
      const causeHeights = causeRows.map(([cause]) => Math.max(12, wrappedHeight(cause, 8.4, causeTextWidth, { leading: 11.2, maxLines: 2 })));
      const causesCaveat = causeRows.length && showCauseWeights;
      const causesHeight = causeRows.length
        ? 22 + causeHeights.reduce((sum, value) => sum + value + 5, 0) + (causesCaveat ? 14 : 0) + 8
        : 0;
      const actionRows = (actions ?? []).slice(0, 4);
      const actionTextWidth = bodyWidth - 22;
      const actionHeights = actionRows.map(([, action]) => Math.max(13, wrappedHeight(action, 8.7, actionTextWidth, { leading: 11.6, maxLines: 2 })));
      const actionsHeight = actionRows.length
        ? 23 + actionHeights.reduce((sum, value) => sum + value + 6, 0) + 6
        : 0;
      const confidenceHeight = confidence ? 16 : 0;
      const cardHeight = 36 + titleHeight
        + (whatHeight ? whatHeight + 6 : 0)
        + (whyHeight ? whyHeight + 6 : 0)
        + (causesHeight ? causesHeight + 6 : 0)
        + (actionsHeight ? actionsHeight + 4 : 0)
        + confidenceHeight
        + 12;
      const top = reserve(cardHeight + 10);
      const bottom = top - cardHeight;
      drawRect(margin, bottom, cardWidth, cardHeight, { fill: pdfPalette.white, stroke: pdfPalette.rule, lineWidth: 0.5 });
      drawRect(margin, bottom, 5, cardHeight, { fill: accent });
      textCommand(String(label ?? "Finding").toUpperCase(), innerX, top - 14, 7.2, true, pdfPalette.muted);
      drawWrapped(titleText, innerX, top - 31, titleSize, titleWidth, { bold: true, leading: titleLeading, maxLines: 2 });
      if (statusLabel) {
        const badgeWidth = Math.max(70, String(statusLabel).length * 5.4 + 22);
        const badgeX = width - margin - badgeWidth - 14;
        drawRect(badgeX, top - 26, badgeWidth, 16, { fill: pdfSoftColorForScore(score), stroke: pdfPalette.rule, lineWidth: 0.35 });
        textCommand(String(statusLabel).toUpperCase(), badgeX + 8, top - 20, 7.6, true, accent);
      }
      if (Number.isFinite(score)) {
        textCommand(`${Math.round(score)}/100`, width - margin - 78, top - 44, 10.5, true, accent);
        scoreBar(width - margin - 86, top - 53, 72, score, { height: 3.5 });
      }

      let sectionTop = top - 32 - titleHeight - 2;

      if (whatHeight) {
        const sectionBottom = sectionTop - whatHeight;
        drawRect(sectionInsetX, sectionBottom, sectionWidth, whatHeight, { fill: pdfPalette.panel, stroke: pdfPalette.rule, lineWidth: 0.3 });
        textCommand("WHAT WE SAW", innerX, sectionTop - 14, 7.2, true, pdfPalette.muted);
        drawWrapped(whatWeSaw, innerX, sectionTop - 30, 9.4, bodyWidth, { leading: 12.4, maxLines: 4, color: pdfPalette.ink, justify: true });
        if (contextLine) {
          const textHeight = wrappedHeight(whatWeSaw, 9.4, bodyWidth, { leading: 12.4, maxLines: 4 });
          textCommand(pdfTruncateText(contextLine, 8.2, bodyWidth), innerX, sectionTop - 32 - textHeight, 8.2, false, pdfPalette.muted);
        }
        sectionTop = sectionBottom - 6;
      }

      if (whyHeight) {
        const sectionBottom = sectionTop - whyHeight;
        drawRect(sectionInsetX, sectionBottom, sectionWidth, whyHeight, { fill: [0.985, 0.99, 1.00], stroke: pdfPalette.rule, lineWidth: 0.3 });
        textCommand("WHY IT MATTERS", innerX, sectionTop - 14, 7.2, true, pdfPalette.muted);
        drawWrapped(whyItMatters, innerX, sectionTop - 30, 9.2, bodyWidth, { leading: 12.2, maxLines: 4, color: pdfPalette.ink, justify: true });
        sectionTop = sectionBottom - 6;
      }

      if (causeRows.length) {
        const sectionBottom = sectionTop - causesHeight;
        drawRect(sectionInsetX, sectionBottom, sectionWidth, causesHeight, { fill: [1.0, 0.985, 0.95], stroke: pdfPalette.rule, lineWidth: 0.3 });
        textCommand(String(causesLabel).toUpperCase(), innerX, sectionTop - 14, 7.2, true, pdfPalette.muted);
        let causeY = sectionTop - 30;
        causeRows.forEach(([cause], index) => {
          const causeHeight = causeHeights[index];
          drawDot(innerX + 4, causeY + 3, 1.8, pdfPalette.quiet);
          drawWrapped(cause, innerX + 16, causeY, 8.4, causeTextWidth, { leading: 11.2, maxLines: 2, color: pdfPalette.ink });
          causeY -= causeHeight + 5;
        });
        if (causesCaveat) {
          textCommand("Other causes are possible.", innerX, causeY - 2, 7.6, false, pdfPalette.quiet);
        }
        sectionTop = sectionBottom - 6;
      }

      if (actionRows.length) {
        const sectionBottom = sectionTop - actionsHeight;
        drawRect(sectionInsetX, sectionBottom, sectionWidth, actionsHeight, { fill: [0.96, 0.98, 1.00], stroke: pdfPalette.rule, lineWidth: 0.3 });
        textCommand("WHAT TO CHECK", innerX, sectionTop - 14, 7.2, true, pdfPalette.muted);
        let actionY = sectionTop - 30;
        actionRows.forEach(([priority, action], index) => {
          const priorityColor = priority === "high" ? pdfPalette.red
            : priority === "medium" ? pdfPalette.amber
            : pdfPalette.muted;
          drawDot(innerX + 4, actionY + 3, 2.4, priorityColor);
          drawWrapped(action, innerX + 16, actionY, 8.7, actionTextWidth, { leading: 11.6, maxLines: 2, color: pdfPalette.ink });
          actionY -= actionHeights[index] + 6;
        });
        sectionTop = sectionBottom - 4;
      }

      if (confidence) {
        textCommand(`Confidence: ${confidence}`, innerX, sectionTop - 8, 7.8, false, pdfPalette.quiet);
      }
      y -= 4;
    },
    shortDisclaimer(text) {
      if (!text) return;
      const h = wrappedHeight(text, 8, contentWidth, { leading: 11 }) + 6;
      const top = reserve(h + 4);
      drawWrapped(text, margin, top - 4, 8, contentWidth, { leading: 11, color: pdfPalette.quiet, justify: true });
      y = top - h;
    },
  };
}
